import axios from 'axios';
import { ApiSalesDashboardService } from 'services';
import {
	ICreditAssignment,
	ICreditAssignmentData,
	ICreditAssignmentsData,
	ICreditRulesCatalog,
	ICreditTerm,
	ICreditTermPayload,
	IResolveDueDateData,
} from './types';

// ─── Envelope de la Support API ──────────────────────────────────────────────

interface KpiEnvelope<T> {
	success: boolean;
	data: T;
	metadata?: { cachedAt?: string; [k: string]: unknown };
}

type Params = Record<string, string | number | boolean | undefined>;

async function kpiGet<T>(path: string, params?: Params): Promise<T> {
	const clean = params ? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) : undefined;
	const res = await ApiSalesDashboardService.get<KpiEnvelope<T>>(path, { params: clean });
	if (!res.data.success) throw new Error(`API error: success=false on ${path}`);
	return res.data.data;
}

async function kpiPost<T>(path: string, body?: unknown): Promise<T> {
	const res = await ApiSalesDashboardService.post<KpiEnvelope<T>>(path, body);
	if (!res.data.success) throw new Error(`API error: success=false on POST ${path}`);
	return res.data.data;
}

async function kpiPatch<T>(path: string, body?: unknown): Promise<T> {
	const res = await ApiSalesDashboardService.patch<KpiEnvelope<T>>(path, body);
	if (!res.data.success) throw new Error(`API error: success=false on PATCH ${path}`);
	return res.data.data;
}

async function kpiPut<T>(path: string, body?: unknown): Promise<T> {
	const res = await ApiSalesDashboardService.put<KpiEnvelope<T>>(path, body);
	if (!res.data.success) throw new Error(`API error: success=false on PUT ${path}`);
	return res.data.data;
}

async function kpiDelete<T>(path: string): Promise<T> {
	const res = await ApiSalesDashboardService.delete<KpiEnvelope<T>>(path);
	if (!res.data.success) throw new Error(`API error: success=false on DELETE ${path}`);
	return res.data.data;
}

/**
 * Mensaje de error legible. Los 400/403/404/409 de este módulo traen mensajes
 * pensados para el usuario (por ejemplo "Tu rol no permite gestionar
 * condiciones de crédito"), así que se muestran tal cual.
 */
export function extractCreditRulesError(err: unknown): string {
	if (axios.isAxiosError(err)) {
		const status = err.response?.status;
		const data = err.response?.data as { message?: string } | undefined;
		if (status && [400, 403, 404, 409, 422].includes(status)) {
			return data?.message ?? `Error ${status}`;
		}
		if (status === 401) return 'Tu sesión expiró — volvé a iniciar sesión';
		if (status && status >= 500) return `Error del servidor (${status}) — intentá de nuevo`;
		if (err.request) return 'No se pudo conectar con el servidor — verificá tu conexión';
	}
	if (err instanceof Error) return err.message;
	return 'Error inesperado';
}

const BASE = 'data/sales/credit-rules';

// ─── Catálogo de formas de regla ─────────────────────────────────────────────

export const getCreditRulesCatalog = (): Promise<ICreditRulesCatalog> => kpiGet<ICreditRulesCatalog>(`${BASE}/catalog`);

// ─── Condiciones ─────────────────────────────────────────────────────────────

export const getCreditTerms = async (includeDisabled = false): Promise<ICreditTerm[]> => {
	const data = await kpiGet<{ terms: ICreditTerm[] }>(`${BASE}/terms`, { includeDisabled });
	return data.terms ?? [];
};

export const postCreditTerm = (body: ICreditTermPayload): Promise<ICreditTerm> =>
	kpiPost<ICreditTerm>(`${BASE}/terms`, body);

export const patchCreditTerm = (id: number, body: ICreditTermPayload): Promise<ICreditTerm> =>
	kpiPatch<ICreditTerm>(`${BASE}/terms/${id}`, body);

// ─── Asignaciones por cliente ────────────────────────────────────────────────

export const getCreditAssignments = (options?: {
	agencyId?: number;
	termId?: number;
	search?: string;
	limit?: number;
	offset?: number;
}): Promise<ICreditAssignmentsData> => kpiGet<ICreditAssignmentsData>(`${BASE}/assignments`, options);

/** Condición vigente de un cliente (o la que regía en `fecha`). */
export const getCreditAssignment = (passengerId: string, fecha?: string): Promise<ICreditAssignmentData> =>
	kpiGet<ICreditAssignmentData>(`${BASE}/assignments/${passengerId}`, { fecha });

export const getCreditAssignmentHistory = async (passengerId: string): Promise<ICreditAssignment[]> => {
	const data = await kpiGet<{ history: ICreditAssignment[] }>(`${BASE}/assignments/${passengerId}/history`);
	return data.history ?? [];
};

export const putCreditAssignment = (
	passengerId: string,
	body: {
		termId: number;
		vigenteDesde?: string;
		notes?: string;
		/**
		 * Identidad del cliente para el snapshot. La API la usa SOLO fuera de
		 * producción: en testing el Power navega clientes de un ambiente y la
		 * Support API lee otro, así que el cliente puede no existir de su lado.
		 * En producción manda la DB del Power y esto se ignora.
		 */
		passengerName?: string;
		passengerDocument?: string;
		agencyId?: number;
	},
): Promise<ICreditAssignmentData> => kpiPut<ICreditAssignmentData>(`${BASE}/assignments/${passengerId}`, body);

export const deleteCreditAssignment = (passengerId: string): Promise<ICreditAssignmentData> =>
	kpiDelete<ICreditAssignmentData>(`${BASE}/assignments/${passengerId}`);

// ─── Cálculo ─────────────────────────────────────────────────────────────────

/** Vencimiento de una factura para un cliente. Lo consume el drawer. */
export const resolveDueDate = (body: {
	passengerId: string;
	fechaFactura?: string;
	fechaEmisionServicio?: string;
	fechaSalidaPrimerServicio?: string;
}): Promise<IResolveDueDateData> => kpiPost<IResolveDueDateData>(`${BASE}/resolve`, body);

/** Preview con parámetros sueltos o un termId. No persiste nada. */
export const previewDueDate = (
	body: ICreditTermPayload & {
		termId?: number;
		fechaFactura?: string;
		fechaEmisionServicio?: string;
		fechaSalidaPrimerServicio?: string;
	},
): Promise<IResolveDueDateData> => kpiPost<IResolveDueDateData>(`${BASE}/preview`, body);
